import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const TOUR_STEP_KEYS = [
  { target: null, titleKey: "tour.welcome", descKey: "tour.welcomeDesc", position: "center", icon: "👋" },
  { target: '[data-testid="sidebar-balance"], [data-testid="module-wallet"]', titleKey: "tour.wallet", descKey: "tour.walletDesc", position: "right", icon: "💰" },
  { target: '[data-testid="nav-dashboard"], [data-testid="module-marketplace"]', titleKey: "tour.dashboard", descKey: "tour.dashboardDesc", position: "right", icon: "📊" },
  { target: '[data-testid="nav-addProperty"], [data-testid="module-add-property"]', titleKey: "tour.addProperty", descKey: "tour.addPropertyDesc", position: "right", icon: "🏠" },
  { target: '[data-testid="nav-properties"], [data-testid="module-my-properties"]', titleKey: "tour.myProperties", descKey: "tour.myPropertiesDesc", position: "right", icon: "📋" },
  { target: '[data-testid="nav-Sold CRM"], [data-testid="module-sold-crm"]', titleKey: "tour.soldCRM", descKey: "tour.soldCRMDesc", position: "right", icon: "🤝" },
  { target: '[data-testid="nav-clientRequirements"], [data-testid="module-client-requirements"]', titleKey: "tour.clientReq", descKey: "tour.clientReqDesc", position: "right", icon: "📋" },
  { target: '[data-testid="nav-Explore Map"], [data-testid="module-explore-map"]', titleKey: "tour.exploreMap", descKey: "tour.exploreMapDesc", position: "right", icon: "🗺" },
  { target: '[data-testid="nav-maps-dropdown"], [data-testid="module-maps"]', titleKey: "tour.maps", descKey: "tour.mapsDesc", position: "right", icon: "📍" },
  { target: '[data-testid="nav-chatWithAI"], [data-testid="module-chat-ai"]', titleKey: "tour.aiChat", descKey: "tour.aiChatDesc", position: "right", icon: "🤖" },
  { target: '[data-testid="nav-settings"], [data-testid="module-profile"]', titleKey: "tour.settings", descKey: "tour.settingsDesc", position: "right", icon: "⚙️" },
  { target: null, titleKey: "tour.allSet", descKey: "tour.allSetDesc", position: "center", icon: "🎉" }
];

export const OnboardingTour = ({ isOpen, onComplete, isDark }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);

  const step = TOUR_STEP_KEYS[currentStep];

  const updateSpotlight = useCallback(() => {
    if (!step?.target) {
      setSpotlightRect(null);
      return;
    }
    const selectors = step.target.split(',').map(s => s.trim());
    let el = null;
    for (const selector of selectors) {
      el = document.querySelector(selector);
      if (el) break;
    }
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlightRect({
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12
      });
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      setSpotlightRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!isOpen) return;
    updateSpotlight();
    window.addEventListener('resize', updateSpotlight);
    return () => window.removeEventListener('resize', updateSpotlight);
  }, [isOpen, currentStep, updateSpotlight]);

  const handleNext = () => {
    if (currentStep < TOUR_STEP_KEYS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleFinish = () => {
    setCurrentStep(0);
    onComplete();
  };

  const handleSkip = () => {
    setCurrentStep(0);
    onComplete();
  };

  if (!isOpen) return null;

  const isCenter = step.position === 'center';
  const isLast = currentStep === TOUR_STEP_KEYS.length - 1;
  const isFirst = currentStep === 0;

  let tooltipStyle = {};
  if (spotlightRect && !isCenter) {
    const isMobileView = window.innerWidth < 640;
    const tooltipWidth = isMobileView ? 280 : 340;
    const tooltipHeight = isMobileView ? 220 : 280;
    const padding = 16;
    let left = spotlightRect.left + spotlightRect.width + padding;
    let top = spotlightRect.top + spotlightRect.height / 2 - tooltipHeight / 2;
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = spotlightRect.left - tooltipWidth - padding;
    }
    if (left < padding) {
      left = Math.max(padding, spotlightRect.left + spotlightRect.width / 2 - tooltipWidth / 2);
      top = spotlightRect.top + spotlightRect.height + padding;
    }
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    tooltipStyle = { position: 'fixed', top, left, zIndex: 10002 };
  }

  return (
    <div className="fixed inset-0 z-[10000]">
      {/* Only show base overlay when there's no spotlight (center steps). 
          For spotlight steps, the box-shadow on the spotlight handles the darkening. */}
      {(!spotlightRect || isCenter) && (
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={handleSkip} />
      )}

      {spotlightRect && !isCenter && (
        <>
          <div className="fixed inset-0" style={{ zIndex: 10000 }} onClick={handleSkip} />
          <div
            className="fixed rounded-lg ring-2 ring-[var(--brand)] ring-offset-2 ring-offset-transparent"
            style={{
              top: spotlightRect.top, left: spotlightRect.left,
              width: spotlightRect.width, height: spotlightRect.height,
              zIndex: 10001, boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)', pointerEvents: 'none'
            }}
          />
        </>
      )}

      <AnimatePresence mode="wait">
        {isCenter ? (
          <motion.div
            key={currentStep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[10002] flex items-center justify-center px-4"
          >
            <div className={`w-full max-w-[280px] sm:max-w-[340px] rounded-xl sm:rounded-2xl shadow-2xl border ${
              isDark ? 'bg-[#2E3944] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}>
              <div className="p-3.5 sm:p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg sm:text-2xl">{step.icon}</span>
                    <span className={`text-[10px] sm:text-xs font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>
                      {currentStep + 1} / {TOUR_STEP_KEYS.length}
                    </span>
                  </div>
                  <button onClick={handleSkip} className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2">{t(step.titleKey)}</h3>
                <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t(step.descKey)}</p>
                <div className={`mt-3 mb-2 sm:mt-4 sm:mb-3 h-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <div className="h-full rounded-full bg-[var(--brand)] transition-all duration-300" style={{ width: `${((currentStep + 1) / TOUR_STEP_KEYS.length) * 100}%` }} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    {!isFirst && <Button variant="ghost" size="sm" onClick={handlePrev} className={isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600'}><ChevronLeft className="w-4 h-4 mr-1" /> {t('tour.back')}</Button>}
                    {isFirst && <Button variant="ghost" size="sm" onClick={handleSkip} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>{t('tour.skipTour')}</Button>}
                  </div>
                  <Button size="sm" onClick={handleNext} className="bg-[var(--brand)] text-white hover:opacity-90">
                    {isLast ? <>{t('tour.getStarted')} <Sparkles className="w-4 h-4 ml-1" /></> : <>{t('tour.next')} <ChevronRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`fixed w-[280px] sm:w-[340px] max-w-[calc(100vw-24px)] rounded-xl sm:rounded-2xl shadow-2xl border ${
              isDark ? 'bg-[#2E3944] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            style={tooltipStyle}
          >
          <div className="p-3.5 sm:p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="text-lg sm:text-2xl">{step.icon}</span>
                <span className={`text-[10px] sm:text-xs font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>
                  {currentStep + 1} / {TOUR_STEP_KEYS.length}
                </span>
              </div>
              <button onClick={handleSkip} className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <h3 className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2">{t(step.titleKey)}</h3>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t(step.descKey)}
            </p>

            <div className={`mt-3 mb-2 sm:mt-4 sm:mb-3 h-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
              <div
                className="h-full rounded-full bg-[var(--brand)] transition-all duration-300"
                style={{ width: `${((currentStep + 1) / TOUR_STEP_KEYS.length) * 100}%` }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                {!isFirst && (
                  <Button variant="ghost" size="sm" onClick={handlePrev} className={isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600'}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> {t('tour.back')}
                  </Button>
                )}
                {isFirst && (
                  <Button variant="ghost" size="sm" onClick={handleSkip} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>
                    {t('tour.skipTour')}
                  </Button>
                )}
              </div>
              <Button size="sm" onClick={handleNext} className="bg-[var(--brand)] text-white hover:opacity-90">
                {isLast ? (
                  <>{t('tour.getStarted')} <Sparkles className="w-4 h-4 ml-1" /></>
                ) : (
                  <>{t('tour.next')} <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
